import React from 'react';
import { Form } from '@edgebox/react-components';

export interface IChipSelectOption<TValue extends string = string> {
  value: TValue;
  label: string;
}

export function ChipsDropdownSelect<TValue extends string>({
  label,
  values,
  options,
  onChange,
  addLabel,
  hideLabel,
}: {
  label: string;
  values: TValue[];
  options: IChipSelectOption<TValue>[];
  onChange: (next: TValue[]) => void;
  addLabel: string;
  hideLabel?: boolean;
}) {
  const [selectedToAdd, setSelectedToAdd] = React.useState('');
  const selectedSet = React.useMemo(() => new Set(values), [values]);

  const selectedOptions = React.useMemo(() => options.filter((option) => selectedSet.has(option.value)), [options, selectedSet]);

  const addableOptions = React.useMemo(() => options.filter((option) => !selectedSet.has(option.value)), [options, selectedSet]);

  return (
    <div>
      {!hideLabel ? <Form.Label className="fw-bold">{label}</Form.Label> : null}

      <div className="d-flex flex-wrap gap-2 mb-2">
        {selectedOptions.map((option) => (
          <span key={option.value} className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs">
            {option.label}
            <button
              type="button"
              className="ml-2 rounded border-0 bg-transparent px-1 py-0 text-slate-500 hover:text-slate-700"
              onClick={() => onChange(values.filter((value) => value !== option.value))}
            >
              X
            </button>
          </span>
        ))}
      </div>

      {/* The label above is not bound to the control, so the control carries the name itself and a
          screen reader announces what the dropdown adds to rather than an unnamed list. */}
      <Form.Select
        aria-label={label}
        value={selectedToAdd}
        onChange={(event) => {
          const nextValue = event.target.value as TValue;
          setSelectedToAdd(nextValue || '');

          if (!nextValue) {
            return;
          }

          onChange([...values, nextValue]);
          setSelectedToAdd('');
        }}
      >
        <option value="">{addLabel}</option>
        {addableOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Form.Select>
    </div>
  );
}
